import { portalText } from "../../i18n/portal.js";
/**
 * Area `messaging` of the console gateway — the Web chat page (plan-004 WC-81, design §17.10.5,
 * IC-37). The four widget routes WC-80 serves, and the web chat detail decoded in full.
 *
 * ── ONE READ FOR THE WHOLE PAGE ──────────────────────────────────────────────────────────
 * `getWebChatDetail` is `getMessagingMedium('web_chat')` decoded against its own contract
 * (`MessagingMediumDetail`, review #175): the shared part through `mediumDetailOfWire` — the same
 * decoder every other medium's page runs — plus every website widget (`WebChatWidget[]`) and the
 * install facts (`WebChatInstall`). The API composes the snippet on the runtime's `rtc` host; the
 * console never composes a tag of its own (review #176).
 *
 * ── A REFUSAL KEEPS ITS REQUEST ID ───────────────────────────────────────────────────────
 * A refused call is the generated client's `ApiError`, which carries `requestId`. A body that
 * answered 2xx but does not decode is a {@link MessagingReadError} carrying the same id, so the page
 * can always say which request to quote — a decoder failure is exactly the bug #175 was, and "no
 * request id" is what made it hard to report.
 *
 * ── WHAT THE PAGE WRITES ─────────────────────────────────────────────────────────────────
 * connect (`site_origin` required; 201 new or 200 the voice agent's existing widget, re-enabled),
 * PATCH `{ enabled?, voice_enabled? }`, PUT the whole origin set, and the widget's own
 * disconnection. Owners and Admins only — the API refuses anyone else with 403, and the page does
 * not offer the controls to them (`CONSOLE_ACT["web_chat_widget.manage"]`).
 */
import { accountTalkRatesOfWire } from "../talk-rate-view.js";
import type { TalkRateView } from "./talk-rate.js";
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway, MessagingMediumDetailView } from "../gateway.js";
import { mediumDetailOfWire } from "../channel-view.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText, readTextArray } from "../decode.js";
import type { SliceContext } from "./slice-context.js";

/** `WebChatWidget.state` — DD4-18's four, from existing columns. */
export type WebsiteWidgetState = "answering" | "needs_origin" | "disabled" | "not_connected";

const WEBSITE_WIDGET_STATE: readonly WebsiteWidgetState[] = ["answering", "needs_origin", "disabled", "not_connected"];

/** One website widget — a `web_chat_widget` channel binding — as the page shows it. */
export interface WebsiteWidgetView {
  readonly channelBindingId: string;
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
  /** Public by construction: it sits in the customer's page source, carries no scope, and is not a secret. */
  readonly widgetKey: string;
  readonly state: WebsiteWidgetState;
  readonly enabled: boolean;
  readonly voiceEnabled: boolean;
  /** `scheme://host[:port]`, normalised by the API, in byte order. */
  readonly allowedOrigin: readonly string[];
  /** The full `<script>` tag, composed by the API on the `rtc` host. */
  readonly snippet: string;
  /** What a site with a Content-Security-Policy adds: `text` for the chat, `voice` added when voice is on. */
  readonly contentSecurityPolicy: { readonly text: readonly string[]; readonly voice: readonly string[] };
}

export interface WebChatInstallView {
  readonly scriptUrl: string;
  /**
   * At the organization's own rates — the API's sentence, never a number written in the console (plan-017: typed
   * minutes at its `text` rate; spoken ones at its `browser` rate too when a widget takes voice and that rate differs).
   */
  readonly billingSentence: string;
  /** plan-017 (OQ-1): the rates a widget's conversations are charged at — `text`, then `browser` when one takes voice. */
  readonly talkRates: readonly TalkRateView[];
}

export interface WebChatDetailView {
  /** The part every medium's page shares: state, availability, stats, the answerable voice agents. */
  readonly medium: MessagingMediumDetailView;
  /** Every website widget of the workspace, in the order they were connected. */
  readonly widget: readonly WebsiteWidgetView[];
  readonly install: WebChatInstallView;
}

export interface WebChatConnectedView {
  /** `true` for a new widget (201); `false` when the voice agent's existing widget came back (200). */
  readonly created: boolean;
  readonly widget: WebsiteWidgetView;
}

export interface WebsiteWidgetChange {
  readonly enabled?: boolean;
  readonly voiceEnabled?: boolean;
}

/** A 2xx answer the console could not decode, with the request id to quote. */
export class MessagingReadError extends Error {
  override readonly name = "MessagingReadError";
  readonly requestId: string | null;
  constructor(cause: Error, requestId: string | null) {
    super(portalText("shell", "wetalk_answered_in_a_way_this_page_cannot_read_value_65d8fa72", { v0: String(cause.message) }));
    this.requestId = requestId;
  }
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The Web chat page: the detail, every website widget and the install facts. Any member reads it. */
    getWebChatDetail(): Promise<WebChatDetailView>;
    /**
     * Connects a website widget for a voice agent on a site. `siteOrigin` is sent as typed; the API
     * normalises it (and refuses it with 400 `origin_invalid` when it is not a site address).
     */
    connectWebChatWidget(voiceAgentId: string, siteOrigin: string): Promise<WebChatConnectedView>;
    /** Turns the widget, its voice, or both on or off. 422 `origin_list_empty` with no allowed site. */
    updateWebChatWidget(channelBindingId: string, change: WebsiteWidgetChange): Promise<WebsiteWidgetView>;
    /** Replaces the whole allowed-origin set (PUT). */
    replaceWebChatWidgetOrigins(channelBindingId: string, allowedOrigin: readonly string[]): Promise<WebsiteWidgetView>;
    /** Off, voice off, origins deleted; History keeps its conversations (DD4-18). */
    disconnectWebChatWidget(channelBindingId: string): Promise<WebsiteWidgetView>;
  }
}

function stateOf(value: unknown, path: string): WebsiteWidgetState {
  const state = readText(value, path);
  const known = WEBSITE_WIDGET_STATE.find((one) => one === state);
  if (known === undefined) {
    throw new MalformedResponseError(path, WEBSITE_WIDGET_STATE.join(" | "), state);
  }
  return known;
}

/** `WebChatWidget` → the view. Every field is required by the contract, so every one is read. */
export function websiteWidgetOfWire(value: unknown, path: string): WebsiteWidgetView {
  const one = readRecord(value, path);
  const policy = readRecord(one["content_security_policy"], `${path}.content_security_policy`);
  return {
    channelBindingId: readText(one["channel_binding_id"], `${path}.channel_binding_id`),
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    voiceAgentName: readText(one["voice_agent_name"], `${path}.voice_agent_name`),
    widgetKey: readText(one["widget_key"], `${path}.widget_key`),
    state: stateOf(one["state"], `${path}.state`),
    enabled: readBoolean(one["enabled"], `${path}.enabled`),
    voiceEnabled: readBoolean(one["voice_enabled"], `${path}.voice_enabled`),
    allowedOrigin: readTextArray(one["allowed_origin"], `${path}.allowed_origin`),
    snippet: readText(one["snippet"], `${path}.snippet`),
    contentSecurityPolicy: {
      text: readTextArray(policy["text"], `${path}.content_security_policy.text`),
      voice: readTextArray(policy["voice"], `${path}.content_security_policy.voice`),
    },
  };
}

/** `MessagingMediumDetail` for web chat → the page's view. `install` must be present for web chat. */
export function webChatDetailOfWire(value: unknown, path: string, now: Date, apiOrigin: string): WebChatDetailView {
  const one = readRecord(value, path);
  const medium = mediumDetailOfWire(value, path, now, apiOrigin);
  if (medium.medium !== "web_chat") {
    throw new MalformedResponseError(`${path}.medium`, "web_chat", medium.medium);
  }
  const install = readRecord(one["install"], `${path}.install`);
  return {
    medium,
    widget: readArray(one["widget"], `${path}.widget`).map((entry, index) =>
      websiteWidgetOfWire(entry, `${path}.widget[${index}]`),
    ),
    install: {
      scriptUrl: readText(install["script_url"], `${path}.install.script_url`),
      billingSentence: readText(install["billing_sentence"], `${path}.install.billing_sentence`),
      talkRates: accountTalkRatesOfWire(install["talk_rates"], `${path}.install.talk_rates`),
    },
  };
}

/**
 * Asserts the call succeeded (an `ApiError` with its request id otherwise), then decodes; a decode
 * failure becomes a {@link MessagingReadError} with the same request id.
 */
function decodedOf<T>(result: ApiResult, decode: (body: unknown) => T): T {
  const body = assertOk(result);
  try {
    return decode(body);
  } catch (error) {
    if (error instanceof MalformedResponseError) throw new MessagingReadError(error, result.requestId);
    throw error;
  }
}

function widgetDataOf(body: unknown, operationId: string): WebsiteWidgetView {
  return websiteWidgetOfWire(readRecord(body, operationId)["data"], `${operationId}.data`);
}

export function messagingApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
  apiOrigin: string,
): Pick<
  ConsoleGateway,
  | "getWebChatDetail"
  | "connectWebChatWidget"
  | "updateWebChatWidget"
  | "replaceWebChatWidgetOrigins"
  | "disconnectWebChatWidget"
> {
  return {
    async getWebChatDetail(): Promise<WebChatDetailView> {
      const result = await client.getMessagingMedium({ path: { medium_code: "web_chat" } });
      return decodedOf(result, (body) =>
        webChatDetailOfWire(readRecord(body, "getMessagingMedium")["data"], "medium", context.now(), apiOrigin),
      );
    },

    async connectWebChatWidget(voiceAgentId: string, siteOrigin: string): Promise<WebChatConnectedView> {
      const result = await client.connectMessagingMedium({
        path: { medium_code: "web_chat" },
        header: { "idempotency-key": crypto.randomUUID() },
        body: { voice_agent_id: voiceAgentId, site_origin: siteOrigin },
      });
      return decodedOf(result, (body) => {
        const data = readRecord(readRecord(body, "connectMessagingMedium")["data"], "connectMessagingMedium.data");
        return {
          created: result.status === 201,
          widget: websiteWidgetOfWire(data["widget"], "connectMessagingMedium.data.widget"),
        };
      });
    },

    async updateWebChatWidget(channelBindingId: string, change: WebsiteWidgetChange): Promise<WebsiteWidgetView> {
      const body: Record<string, boolean> = {};
      if (change.enabled !== undefined) body["enabled"] = change.enabled;
      if (change.voiceEnabled !== undefined) body["voice_enabled"] = change.voiceEnabled;
      const result = await client.updateWebChatWidget({ path: { channel_binding_id: channelBindingId }, body });
      return decodedOf(result, (answer) => widgetDataOf(answer, "updateWebChatWidget"));
    },

    async replaceWebChatWidgetOrigins(
      channelBindingId: string,
      allowedOrigin: readonly string[],
    ): Promise<WebsiteWidgetView> {
      const result = await client.replaceWebChatWidgetOrigins({
        path: { channel_binding_id: channelBindingId },
        body: { allowed_origin: [...allowedOrigin] },
      });
      return decodedOf(result, (answer) => widgetDataOf(answer, "replaceWebChatWidgetOrigins"));
    },

    async disconnectWebChatWidget(channelBindingId: string): Promise<WebsiteWidgetView> {
      const result = await client.disconnectWebChatWidget({
        path: { channel_binding_id: channelBindingId },
        header: { "idempotency-key": crypto.randomUUID() },
      });
      return decodedOf(result, (answer) => widgetDataOf(answer, "disconnectWebChatWidget"));
    },
  };
}
